import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material'

import { FC, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { ArrowBackIosNew, InfoOutlined } from '@mui/icons-material'
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined'
import { LoadingButton } from '@mui/lab'
import axios, { AxiosError } from 'axios'
import { StatusCodes } from 'http-status-codes'
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input'
import { useLocation, useNavigate } from 'react-router-dom'
import { fullName } from '../../domain/Account/AccountDisplay'
import { AdoptersClient } from '../../interactors/clients/AdoptersClient'
import {
  AccountDtoPermission,
  AdopterDto,
  AdopterDtoProfileHasBeenAccepted,
  AnimalAdoptionDtoStatus,
  CreateAdopterBodyDto,
  AnimalAdoptionDtoOfferType,
} from '../../interactors/gen/backendClient'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { readableAxiosError } from '../../utils/axios'
import { omitEmptyString } from '../../utils/omitEmptyString'
import { PATHS } from '../PATHS'
import { theme } from '../theme'
import { ADOPTION_REQUEST_CHIP, AdoptionTypeCard } from './AdoptionTypeCard'
import { AdoptionContext as FastLaunchAdoptionContext, FastLaunchProcedureDialog } from './FastLaunchProcedureDialog'
import { useAccountsStore } from '../../store/AccountsStore'
import { ControlledSelectField } from '../common/ControlledSelectField'
import { animalAdoptionOfferTypeOrder, getReadableAnimalAdoptionOfferType } from '../../domain/Animal/AnimalDisplay'
import { ControlledDateField } from '../common/ControlledDateField'
import { isValidPastDateCoherence } from '../../utils/date/isValidPastDateCoherence'
import { ColoredChipSelectField } from '../common/ColoredChipSelectField'
import { setOpacity } from '../../utils/setOpacity'
import { useStripeConnectStatus } from '../../utils/hooks/useStripeConnectStatus'
import { StripeConnectRequiredStep } from './StripeConnectRequiredStep'
import { DetailSurface } from '../common/DetailSurface'

// We need to omit the select and add empty string, if not it does not work
type CreateAdopterFormParams = CreateAdopterBodyDto

type AdoptionIntentFormParams = {
  adoptionType: 'request' | 'past' | ''
  offerType?: AnimalAdoptionDtoOfferType
  priceInEuros?: number
  hasDeposit: boolean
  depositDate?: string
  depositAmount?: number
  depositStatus?: 'true' | 'false'
  prepareDocuments: boolean
}

type AdoptionContext = FastLaunchAdoptionContext

type AddAdopterScreenLocationState = {
  fromAnimalAdoption?: {
    animalId: string
    status?: AnimalAdoptionDtoStatus
    adoptionType?: 'request' | 'past'
    offerType?: AnimalAdoptionDtoOfferType | null
    priceInEuros?: number | null
    hasDeposit?: boolean
    depositDate?: string
    depositAmount?: number
    depositStatus?: 'true' | 'false'
    prepareDocuments?: boolean
  }
}

export const AddAdopterScreen: FC = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const adoptionTypeFromQuery = queryParams.get('adoptionType')
  const offerTypeFromQuery = queryParams.get('offerType') as AnimalAdoptionDtoOfferType | null
  const priceInEurosFromQuery = queryParams.get('priceInEuros')
  const [currentStep, setCurrentStep] = useState<'adoption' | 'info'>('adoption')
  const [isLoading, setIsLoading] = useState(false)
  const globalSnackbarStore = useGlobalSnackbarStore()
  const navigate = useNavigate()
  const locationState = location.state as AddAdopterScreenLocationState | null
  const fromAnimalAdoption = locationState?.fromAnimalAdoption
  const accountsStore = useAccountsStore()
  const {
    register: registerAdoption,
    control: adoptionControl,
    handleSubmit: handleAdoptionSubmit,
    formState: { errors: adoptionErrors },
    watch: watchAdoptionForm,
    setValue: setAdoptionValue,
  } = useForm<AdoptionIntentFormParams>({
    defaultValues: {
      adoptionType: '',
      hasDeposit: false,
      depositStatus: 'true',
      prepareDocuments: accountsStore.isPremium(),
      offerType: AnimalAdoptionDtoOfferType.HealthCostCompensation,
    },
  })

  const {
    register: registerAdopter,
    control: adopterControl,
    handleSubmit: handleAdopterSubmit,
    reset,
    formState: { errors: adopterErrors },
  } = useForm<CreateAdopterFormParams>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      address: '',
      postalCode: '',
      city: '',
      country: 'France',
      sendForm: false,
    },
    mode: 'onChange',
  })

  const [userWantToAssignPastAdoption, setUserWantToAssignPastAdoption] = useState<boolean>(false)
  const [openSteps, setOpenSteps] = useState(false)
  const [adopter, setAdopter] = useState<AdopterDto | null>(null)
  const [adoptionContext, setAdoptionContext] = useState<AdoptionContext>({
    hasDeposit: false,
    prepareDocuments: accountsStore.isPremium(),
  })
  const hasHydratedPrepareDocumentsRef = useRef(false)

  const sendEmailToAdopter = true

  useEffect(() => {
    const adoptionTypeFromState = fromAnimalAdoption?.adoptionType
    if (adoptionTypeFromState === 'past' || adoptionTypeFromQuery === 'past') {
      setAdoptionValue('adoptionType', 'past')
      setUserWantToAssignPastAdoption(true)
      setCurrentStep('info')
    }

    if (adoptionTypeFromState === 'request') {
      setAdoptionValue('adoptionType', 'request')
    }

    const initialOfferType = fromAnimalAdoption?.offerType || offerTypeFromQuery
    if (initialOfferType) {
      setAdoptionValue('offerType', initialOfferType)
    }

    const initialPriceInEuros =
      fromAnimalAdoption?.priceInEuros ?? (priceInEurosFromQuery ? Number(priceInEurosFromQuery) : undefined)
    if (initialPriceInEuros != null && !Number.isNaN(Number(initialPriceInEuros))) {
      setAdoptionValue('priceInEuros', Number(initialPriceInEuros))
    }

    if (fromAnimalAdoption?.hasDeposit != null) {
      setAdoptionValue('hasDeposit', fromAnimalAdoption.hasDeposit)
      setAdoptionValue('depositDate', fromAnimalAdoption.hasDeposit ? fromAnimalAdoption.depositDate : undefined)
      setAdoptionValue('depositAmount', fromAnimalAdoption.hasDeposit ? fromAnimalAdoption.depositAmount : undefined)
      setAdoptionValue(
        'depositStatus',
        fromAnimalAdoption.hasDeposit && fromAnimalAdoption.depositStatus === 'false' ? 'false' : 'true'
      )
    }
  }, [adoptionTypeFromQuery, fromAnimalAdoption, offerTypeFromQuery, priceInEurosFromQuery, setAdoptionValue])

  const [serverError, setServerError] = useState('')

  const isReader = accountsStore.connectedAccount?.permission === AccountDtoPermission.Read
  const isNotAdmin = !accountsStore.isAdmin()
  const editorsCanCreateAdoptions = accountsStore.connectedCustomer?.editorsCanCreateAdoptions === true
  const isPremium = accountsStore.isPremium()
  const isCustomerConfigurationLoading = accountsStore.connectedCustomer == null
  const { status: stripeStatus, refresh: refreshStripeStatus } = useStripeConnectStatus(
    currentStep === 'info' && !userWantToAssignPastAdoption
  )

  const adoptionType = watchAdoptionForm('adoptionType')
  const hasDeposit = watchAdoptionForm('hasDeposit')
  const offerType = watchAdoptionForm('offerType')
  const adoptionPriceInEuros = watchAdoptionForm('priceInEuros')
  const depositAmountInEuros = watchAdoptionForm('depositAmount')
  const depositStatus = watchAdoptionForm('depositStatus')
  const isDonation = offerType === AnimalAdoptionDtoOfferType.Donation
  const isFreeAmount = offerType === AnimalAdoptionDtoOfferType.FreeAmount
  const isAdoptionRequest = adoptionType === 'request'
  const priceFieldLabel = isFreeAmount ? 'Montant minimum (min. 1€)' : "Montant de l'adoption"

  const formatEuros = (value?: number | null) => {
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n)) return '--'
    return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`
  }

  const remainingAmountInEuros = (() => {
    const price = typeof adoptionPriceInEuros === 'number' ? adoptionPriceInEuros : Number(adoptionPriceInEuros)
    const deposit = typeof depositAmountInEuros === 'number' ? depositAmountInEuros : Number(depositAmountInEuros)
    const safePrice = Number.isFinite(price) ? price : 0
    const safeDeposit = Number.isFinite(deposit) ? deposit : 0
    const effectivePrice = isFreeAmount
      ? Math.max(safePrice || 1, depositStatus === 'true' ? safeDeposit : 0)
      : safePrice

    // Remaining amount is reduced only if a deposit exists AND is validated.
    if (hasDeposit && depositStatus === 'true') {
      return Math.max(effectivePrice - safeDeposit, 0)
    }

    // No deposit or deposit pending => full price is still requested.
    return effectivePrice
  })()

  useEffect(() => {
    if (!offerType) {
      setAdoptionValue('offerType', AnimalAdoptionDtoOfferType.HealthCostCompensation)
    }
  }, [offerType, setAdoptionValue])

  useEffect(() => {
    if (fromAnimalAdoption?.prepareDocuments != null) {
      hasHydratedPrepareDocumentsRef.current = true
      setAdoptionValue('prepareDocuments', fromAnimalAdoption.prepareDocuments)
      return
    }

    if (!isPremium) {
      setAdoptionValue('prepareDocuments', false)
      return
    }

    if (hasHydratedPrepareDocumentsRef.current) {
      return
    }

    hasHydratedPrepareDocumentsRef.current = true
    setAdoptionValue('prepareDocuments', true)
  }, [fromAnimalAdoption?.prepareDocuments, isPremium, setAdoptionValue])

  const handleAdoptionTypeToggle = (newValue: 'request' | 'past' | '') => {
    setAdoptionValue('adoptionType', newValue)
    if (newValue === 'past') {
      setUserWantToAssignPastAdoption(true)
      setCurrentStep('info')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      setUserWantToAssignPastAdoption(false)
      setCurrentStep('adoption')
    }
  }

  useEffect(() => {
    if (isDonation) {
      setAdoptionValue('priceInEuros', 0)
    }
  }, [isDonation, setAdoptionValue])

  useEffect(() => {
    if (
      isFreeAmount &&
      (adoptionPriceInEuros === undefined || adoptionPriceInEuros === null || Number(adoptionPriceInEuros) < 1)
    ) {
      setAdoptionValue('priceInEuros', 1, { shouldValidate: true })
    }
  }, [adoptionPriceInEuros, isFreeAmount, setAdoptionValue])

  useEffect(() => {
    if (!hasDeposit) {
      setAdoptionValue('depositDate', undefined)
      setAdoptionValue('depositAmount', undefined)
      setAdoptionValue('depositStatus', 'true')
    }
  }, [hasDeposit, setAdoptionValue])

  const handleAdoptionStepSubmit = (data: AdoptionIntentFormParams) => {
    if (isCustomerConfigurationLoading) {
      return
    }

    const isRequest = data.adoptionType === 'request'
    const isDonationSelection = data.offerType === AnimalAdoptionDtoOfferType.Donation
    const shouldPrepareDocuments = isRequest && isPremium && data.prepareDocuments
    setUserWantToAssignPastAdoption(!isRequest)
    setAdoptionContext({
      offerType: isRequest ? data.offerType : undefined,
      priceInEuros: isRequest
        ? isDonationSelection
          ? 0
          : data.priceInEuros == null || Number.isNaN(data.priceInEuros)
          ? undefined
          : data.priceInEuros
        : undefined,
      hasDeposit: isRequest ? data.hasDeposit : false,
      depositDate: isRequest && data.hasDeposit ? data.depositDate : undefined,
      depositAmount: isRequest && data.hasDeposit ? data.depositAmount : undefined,
      depositStatus: isRequest && data.hasDeposit ? (data.depositStatus === 'true' ? true : false) : undefined,
      prepareDocuments: shouldPrepareDocuments,
    })
    setCurrentStep('info')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const onSubmit = async (data: CreateAdopterFormParams) => {
    setIsLoading(true)
    console.log('On submit', data)

    if (!userWantToAssignPastAdoption && stripeStatus !== 'connected') {
      setIsLoading(false)
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataWithoutEmptyString = omitEmptyString(data as any)

    if (userWantToAssignPastAdoption) {
      dataWithoutEmptyString.profileHasBeenAccepted = AdopterDtoProfileHasBeenAccepted.Accepted
    }

    const from = queryParams.get('from')

    await AdoptersClient.createAdopter(dataWithoutEmptyString as CreateAdopterBodyDto)
      .then((account) => {
        console.log('✨ Adopter created!')

        globalSnackbarStore.triggerSuccessMessage(`Adoptant ${fullName(data)} a été créé.`)

        if (!from && !userWantToAssignPastAdoption) {
          setAdopter(account)
          setOpenSteps(true)
          reset()
          return
        }

        if (from && from.length > 0) {
          const returnParams = new URLSearchParams()
          returnParams.set('adoptant', account.id)

          const card = queryParams.get('card')
          if (card) {
            returnParams.set('card', card)
          }

          const nextStatus =
            fromAnimalAdoption?.status || (queryParams.get('status') as AnimalAdoptionDtoStatus | null) || undefined
          if (nextStatus) {
            returnParams.set('status', nextStatus)
          }

          const nextOfferType = adoptionContext.offerType ?? fromAnimalAdoption?.offerType ?? undefined
          if (nextOfferType) {
            returnParams.set('offerType', nextOfferType)
          }

          const nextPriceInEuros = adoptionContext.priceInEuros ?? fromAnimalAdoption?.priceInEuros ?? undefined
          if (nextPriceInEuros != null && !Number.isNaN(Number(nextPriceInEuros))) {
            returnParams.set('priceInEuros', String(nextPriceInEuros))
          }

          return navigate(`/${from}${from.includes('?') ? '&' : '?'}${returnParams.toString()}`, {
            state: {
              fromAddAdopter: {
                adopterId: account.id,
                status: nextStatus,
                adoptionContext,
              },
            },
          })
        }
        if (userWantToAssignPastAdoption) {
          return navigate(
            `${PATHS.assignerAnimal.absolute}?adopterId=${
              account.id
            }&type=pastAdoption&sendEmailToAdopter=${sendEmailToAdopter.toString()}`
          )
        }
      })
      .catch((error: Error | AxiosError) => {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === StatusCodes.CONFLICT) {
            setIsLoading(false)
            setServerError('Un utilisateur existe déjà avec cette adresse mail.')
            return
          }
        }

        setServerError(readableAxiosError(error).join(' '))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const spacingItem = { marginTop: 1.5 }
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      boxShadow: '0 1px 2px rgba(32, 56, 100, 0.03)',
      minHeight: 44,
      alignItems: 'center',
    },
    '& .MuiOutlinedInput-input': {
      padding: '11px 12px',
      lineHeight: 1.25,
    },
    '& .MuiInputLabel-root': {
      fontWeight: 400,
      transform: 'translate(14px, 11px) scale(1)',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  }
  return (
    <>
      <DetailSurface sx={{ maxWidth: '860px', my: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {currentStep === 'info' && (
            <Tooltip title="Revenir au choix du type d'adoption">
              <IconButton
                size="small"
                onClick={() => {
                  setAdoptionValue('adoptionType', 'request')
                  setUserWantToAssignPastAdoption(false)
                  setCurrentStep('adoption')
                }}
              >
                <ArrowBackIosNew fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography sx={{ fontSize: { xs: '1.15rem', md: '1.45rem' }, fontWeight: 900, lineHeight: 1.1 }}>
                Nouvelle adoption
              </Typography>
              <Tooltip title="L'intérêt d'ajouter une adoption passée est de pouvoir conserver l'historique des adoptions de l'adoptant sur Petso.">
                <InfoOutlined sx={{ color: theme.palette.primary.main }} fontSize="small" />
              </Tooltip>
            </Box>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#7A8197', mt: 0.5 }} />
          </Box>
        </Box>

        {currentStep === 'adoption' ? (
          <form onSubmit={handleAdoptionSubmit(handleAdoptionStepSubmit)}>
            <Controller
              name="adoptionType"
              control={adoptionControl}
              rules={{ validate: (value) => (value ? true : "Vous devez choisir un type d'adoption.") }}
              render={({ field, fieldState }) => (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                  <AdoptionTypeCard
                    title="Une demande d'adoption"
                    chip={ADOPTION_REQUEST_CHIP}
                    selected={field.value === 'request'}
                    description={
                      <>
                        Le statut de l&apos;animal choisi passera en cours d&apos;adoption.
                        <Box component="span" sx={{ display: 'block', mt: 0.35 }}>
                          → Un lien de paiement du montant de l&apos;adoption sera également transmis à l&apos;adoptant
                          pour règlement.
                        </Box>
                      </>
                    }
                    onClick={() => {
                      field.onChange('request')
                      handleAdoptionTypeToggle('request')
                    }}
                  />
                  <AdoptionTypeCard
                    title="Une adoption passée"
                    selected={field.value === 'past'}
                    description="Enregistre l'historique d'une adoption déjà réalisée, sans paiement en ligne."
                    onClick={() => {
                      field.onChange('past')
                      handleAdoptionTypeToggle('past')
                    }}
                  />
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={{ color: '#7A8197', fontSize: '0.82rem', fontWeight: 500 }}>
                      {field.value === '' && 'Choisissez le type de parcours à lancer.'}
                    </Typography>
                    {fieldState.error && (
                      <Typography sx={{ mt: 0.5, color: 'error.main', fontSize: '0.75rem', fontWeight: 600 }}>
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />

            {isAdoptionRequest && (
              <>
                {isCustomerConfigurationLoading && (
                  <Box
                    sx={{
                      ...spacingItem,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 2,
                      border: `1px solid ${setOpacity(theme.palette.info.main, 0.3)}`,
                      backgroundColor: setOpacity(theme.palette.info.main, 0.08),
                    }}
                  >
                    <CircularProgress size={18} />
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#5D6785' }}>
                      Chargement de votre configuration Petso…
                    </Typography>
                  </Box>
                )}

                {!accountsStore.connectedCustomer?.stripeAccountId && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.25,
                      borderRadius: 3,
                      border: '1px solid rgba(240, 61, 61, 0.25)',
                      backgroundColor: 'rgba(240, 61, 61, 0.06)',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'error.main' }}>
                      Action requise: configurez Stripe Connect pour autoriser la reception des reglements en ligne.
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    mt: '30px',
                    pt: 0.25,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: '#171F34' }}>
                      Information du paiement
                    </Typography>
                    <Typography sx={{ mt: 0.2, fontSize: '0.72rem', fontWeight: 600, color: '#7A8197' }}>
                      Un lien de paiement en ligne sera envoyé sur le mail de l&apos;adoptant
                    </Typography>
                  </Box>
                  <ControlledSelectField
                    control={adoptionControl}
                    error={adoptionErrors.offerType}
                    fieldName="offerType"
                    label="Nature de l'offre"
                    options={animalAdoptionOfferTypeOrder.map((field: AnimalAdoptionDtoOfferType) => ({
                      label: getReadableAnimalAdoptionOfferType(field),
                      value: field,
                    }))}
                    requiredRule="Vous devez renseigner la nature de l'offre."
                    size="small"
                    sx={fieldSx}
                  />

                  <TextField
                    id="price-input"
                    label={priceFieldLabel}
                    type="number"
                    inputProps={{
                      step: '0.01',
                      inputMode: 'decimal',
                      pattern: '[0-9]*',
                      min: isFreeAmount ? 1 : 0,
                    }}
                    fullWidth
                    {...registerAdoption('priceInEuros', {
                      valueAsNumber: true,
                      validate: (value) => {
                        if (!isAdoptionRequest || isDonation) return true
                        if (isFreeAmount && (value === undefined || value === null || Number.isNaN(value))) {
                          return 'Le montant minimum est requis'
                        }
                        if (isFreeAmount && Number(value) < 1) {
                          return 'Le montant minimum doit être au moins de 1€'
                        }
                        if (value === undefined || value === null || Number.isNaN(value)) {
                          return "Le montant de l'adoption est requis"
                        }
                        if (value < 0) {
                          return 'Le montant doit être positif'
                        }
                        return true
                      },
                    })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                    size="small"
                    disabled={isDonation}
                    error={!!adoptionErrors.priceInEuros}
                    helperText={adoptionErrors.priceInEuros?.message}
                  />
                </Box>

                <Box
                  sx={{
                    ...spacingItem,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1.25,
                    ml: 0.875,
                    mt: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#7A8197' }}>
                    Un acompte a-t-il déjà été réalisé ?
                  </Typography>
                  <Controller
                    name="hasDeposit"
                    control={adoptionControl}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                        sx={{ ml: 0.25 }}
                      />
                    )}
                  />
                </Box>

                {hasDeposit && (
                  <Box
                    sx={{
                      ...spacingItem,
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                      gap: 2,
                    }}
                  >
                    <ControlledDateField
                      control={adoptionControl}
                      error={adoptionErrors.depositDate}
                      fieldName="depositDate"
                      label="Date de l'acompte"
                      requiredRule="Vous devez renseigner la date de l'acompte."
                      size="small"
                      validate={(value) => isValidPastDateCoherence(value) || value === null}
                      sx={fieldSx}
                    />
                    <TextField
                      id="deposit-amount-input"
                      label="Montant de l'acompte"
                      type="number"
                      inputProps={{
                        step: '0.01',
                        inputMode: 'decimal',
                        pattern: '[0-9]*',
                      }}
                      fullWidth
                      {...registerAdoption('depositAmount', {
                        valueAsNumber: true,
                        validate: (value) => {
                          if (!hasDeposit) return true
                          if (value === undefined || value === null || Number.isNaN(value)) {
                            return "Le montant de l'acompte est requis"
                          }
                          if (value < 0) {
                            return 'Le montant doit être positif'
                          }
                          return true
                        },
                      })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">€</InputAdornment>,
                      }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      error={!!adoptionErrors.depositAmount}
                      helperText={adoptionErrors.depositAmount?.message}
                      sx={fieldSx}
                    />
                    <ColoredChipSelectField
                      control={adoptionControl}
                      error={adoptionErrors.depositStatus}
                      fieldName="depositStatus"
                      label="État de l'acompte"
                      options={[
                        { label: 'Validé', value: 'true', color: theme.palette.success.main },
                        { label: 'En attente', value: 'false', color: theme.palette.warning.main },
                      ]}
                      requiredRule=""
                      size="small"
                      sx={fieldSx}
                    />
                  </Box>
                )}

                <Box sx={{ mt: 1.5, ml: 0.875 }}>
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 900, color: '#171F34' }}>
                    {isFreeAmount
                      ? 'Montant minimum restant demandé à l’adoptant'
                      : 'Montant restant demandé à l’adoptant'}{' '}
                    : {formatEuros(remainingAmountInEuros)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    ...spacingItem,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: setOpacity(theme.palette.info.main, 0.08),
                    border: `1px solid ${setOpacity(theme.palette.info.main, 0.3)}`,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: 'text.primary',
                        fontSize: '0.95rem',
                        lineHeight: 1.2,
                      }}
                    >
                      Générez et envoyez le certificat et le contrat d&apos;adoption pour signature.
                    </Typography>
                    <Typography
                      variant="body2"
                      fontStyle="italic"
                      color="text.primary"
                      sx={{ mt: 0.25, fontSize: '0.8rem', color: '#7A8197', lineHeight: 1.25 }}
                    >
                      Modèles configurables. Notification de réception. Suivi et validation dans l&apos;onglet adoptant.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={isPremium ? '' : 'Réservé au plan Petso Premium'}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Typography sx={{ fontWeight: 700, color: '#7A8197', fontSize: '0.75rem' }}>
                          {watchAdoptionForm('prepareDocuments') ? 'Activé' : 'Désactivé'}
                        </Typography>
                        <Switch
                          checked={watchAdoptionForm('prepareDocuments')}
                          onChange={(e) => setAdoptionValue('prepareDocuments', e.target.checked)}
                          color="warning"
                          disabled={!isPremium}
                          inputProps={{
                            title: isPremium ? '' : 'Réservé au plan Petso Premium',
                          }}
                        />
                        <span role="img" aria-label="crown">
                          👑
                        </span>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 4 }}>
              <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
                Retour
              </Button>
              <Button variant="contained" color="primary" type="submit" disabled={isCustomerConfigurationLoading}>
                {isCustomerConfigurationLoading ? 'Chargement…' : 'Suivant'}
              </Button>
            </Box>
          </form>
        ) : !userWantToAssignPastAdoption && stripeStatus !== 'connected' ? (
          stripeStatus === 'loading' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              padding={3}
              sx={{ width: '100%', height: '100%' }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <StripeConnectRequiredStep
              isAdmin={accountsStore.isAdmin()}
              onClose={() => {
                setAdoptionValue('adoptionType', 'request')
                setUserWantToAssignPastAdoption(false)
                setCurrentStep('adoption')
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              onRefresh={refreshStripeStatus}
            />
          )
        ) : (
          <form onSubmit={handleAdopterSubmit((data) => onSubmit(data))}>
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: '#171F34' }}>
                Coordonnées de l&apos;adoptant
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 1.1,
                  borderRadius: 3,
                  border: '1px solid #F0DE9D',
                  backgroundColor: '#FFF5D6',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                }}
              >
                <LightbulbOutlined sx={{ color: '#FCB402', mt: '2px' }} fontSize="small" />
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#171F34', lineHeight: 1.35 }}>
                  Adoptant déjà enregistré ? Rendez vous dans l&apos;onglet adoptant et cliquez sur &quot;Nouvelle
                  adoption&quot; dans sa fiche.
                </Typography>
              </Box>
            </Box>
            <TextField
              id="first-name-input"
              label="Prénom"
              type="text"
              fullWidth
              {...registerAdopter('firstName', { required: 'Le prénom est requis' })}
              sx={{ ...spacingItem, ...fieldSx }}
              error={!!adopterErrors.firstName}
              helperText={adopterErrors.firstName?.message}
              required
            />

            <TextField
              id="last-name-input"
              label="Nom de Famille"
              type="text"
              fullWidth
              {...registerAdopter('lastName', { required: 'Le nom est requis' })}
              sx={{ ...spacingItem, ...fieldSx }}
              error={!!adopterErrors.lastName}
              helperText={adopterErrors.lastName?.message}
              required
            />

            <TextField
              id="email"
              label="Email"
              type="email"
              fullWidth
              {...registerAdopter('email', {
                required: "L'email est requis.",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "L'addresse n'est pas valide.",
                },
              })}
              sx={{ ...spacingItem, ...fieldSx }}
              error={!!adopterErrors.email}
              helperText={adopterErrors.email?.message}
              required
            />

            <Typography sx={{ mt: 2, fontSize: '0.82rem', fontWeight: 800, color: '#7A8197' }}>
              Informations facultatives (utiles pour la génération des documents)
            </Typography>

            <FormControl fullWidth variant="outlined" sx={{ ...spacingItem, ...fieldSx }}>
              <Controller
                name="phoneNumber"
                control={adopterControl}
                rules={{
                  validate: (value: string | undefined) => (!value || matchIsValidTel(value) ? true : 'Tel is invalid'),
                }}
                render={({ field, fieldState }) => (
                  <MuiTelInput
                    {...field}
                    label="Numero de Téléphone"
                    defaultCountry="FR"
                    helperText={fieldState.invalid ? "Le numero de téléphone n'est pas valide" : ''}
                    error={fieldState.invalid}
                  />
                )}
              />
            </FormControl>

            <TextField
              id="address"
              label="Adresse"
              type="text"
              fullWidth
              {...registerAdopter('address')}
              sx={{ ...spacingItem, ...fieldSx }}
              error={!!adopterErrors.address}
              helperText={adopterErrors.address?.message}
            />

            <Box
              sx={{
                ...spacingItem,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <TextField
                id="postalCode"
                label="Code Postal"
                type="text"
                fullWidth
                {...registerAdopter('postalCode')}
                sx={fieldSx}
                error={!!adopterErrors.postalCode}
                helperText={adopterErrors.postalCode?.message}
              />

              <TextField
                id="city"
                label="Ville"
                type="text"
                fullWidth
                {...registerAdopter('city')}
                sx={fieldSx}
                error={!!adopterErrors.city}
                helperText={adopterErrors.city?.message}
              />

              <TextField
                id="country"
                label="Pays"
                type="text"
                fullWidth
                {...registerAdopter('country')}
                sx={fieldSx}
                error={!!adopterErrors.country}
                helperText={adopterErrors.country?.message}
              />
            </Box>

            {serverError && (
              <Box sx={spacingItem}>
                <Typography color="error">{serverError}</Typography>
              </Box>
            )}

            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', ...spacingItem }}>
              <Tooltip
                title={
                  isNotAdmin && !editorsCanCreateAdoptions ? `L'administrateur ne vous a pas autorisé cet accès` : ``
                }
              >
                <span style={{ width: '100%' }}>
                  <LoadingButton
                    variant="contained"
                    sx={{ width: '100%' }}
                    type="submit"
                    loading={isLoading}
                    disabled={isReader || (isNotAdmin && !editorsCanCreateAdoptions)}
                  >
                    Ajouter
                  </LoadingButton>
                </span>
              </Tooltip>
            </Box>
          </form>
        )}
      </DetailSurface>
      <FastLaunchProcedureDialog
        open={openSteps}
        adopter={adopter}
        setOpen={setOpenSteps}
        sendEmailToAdopter={sendEmailToAdopter}
        adoptionContext={adoptionContext}
      />
    </>
  )
}
